import { useEffect, useState } from 'react';
import ReactPixel from 'react-facebook-pixel';
import {
	FaArrowRight,
	FaCheckCircle,
	FaChevronDown,
	FaClock,
	FaComments,
	FaGamepad,
	FaShieldAlt,
	FaShoppingBag,
	FaUserPlus,
} from 'react-icons/fa';

import '../App.css';

// Import images
import LogoImg from '@/assets/static-landing-pages/Logo.png';
import AiShakehandImg from '@/assets/static-landing-pages/ai-shakehand.png';
import BulbImg from '@/assets/static-landing-pages/bulb.png';
import CircuitImg from '@/assets/static-landing-pages/circuit.png';
import JoinImageImg from '@/assets/static-landing-pages/join-image.png';
import MailchimpFormBgImg from '@/assets/static-landing-pages/mailchimp-form-bg.jpg';
import NegotiateGirlImg from '@/assets/static-landing-pages/negotiate-girl.png';
import RobotSlidesImg from '@/assets/static-landing-pages/robot-slides.png';
import SamsungFoldImg from '@/assets/static-landing-pages/samsung-fold.png';

// Add smooth scrolling behavior
const scrollToSection = (sectionId: string) => {
	const element = document.getElementById(sectionId);
	if (element) {
		element.scrollIntoView({ behavior: 'smooth' });
	}
};

function Welcome() {
	const [openFAQ, setOpenFAQ] = useState<number | null>(null);

	// Initialize Meta Pixel
	useEffect(() => {
		ReactPixel.init('1106122598296027');
		ReactPixel.pageView();
	}, []);

	const toggleFAQ = (index: number) => {
		setOpenFAQ(openFAQ === index ? null : index);
	};

	const faqData = [
		{
			question: 'Do I have to pay to join?',
			answer: "No, it's completely free to sign up and access Haggle events.",
		},
		{
			question: 'What kind of merchants are on Haggle?',
			answer:
				'A mix of big-name retailers and boutique brands across fashion, beauty, home, sport, electronics, travel and more.',
		},
		{
			question: 'Is Haggle safe to use?',
			answer:
				'Absolutely. Every merchant is vetted before joining, and all payments are processed through secure global payment platforms. Your card details are never stored by Haggle.',
		},
		{
			question: 'How often are events?',
			answer:
				"Most events run for just a few hours, once or twice a month per category. When they're gone, they're gone.",
		},
		{
			question: 'Is Haggle just for bargain hunters?',
			answer:
				"Not at all! Haggle is for anyone who wants to get better value for their money. Whether you're looking for premium products or everyday items, our platform helps you negotiate fair prices that work for your budget.",
		},
	];

	return (
		<div className="min-h-screen bg-white w-full">
			{/* Hero Section */}
			<section
				id="hero"
				className="relative py-10 sm:py-20 overflow-hidden min-h-screen flex"
				style={{
					backgroundImage: `url(${AiShakehandImg})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				{/* Overlay for better text readability */}
				<div className="absolute inset-0 bg-black opacity-20"></div>

				<div className="px-5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 flex flex-col justify-around">
					<div className="text-center">
						{/* Haggle Logo */}
						<div className="mb-6 sm:mb-8">
							<img
								src={LogoImg}
								alt="Haggle"
								className="h-16 sm:h-20 md:h-24 lg:h-32 w-auto mx-auto"
							/>
						</div>
					</div>

					{/* Business and Customer Sections */}
					<div className="text-center">
						{/* Main Headline */}
						<div>
							<h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-white mb-8 sm:mb-12 lg:mb-16 px-4">
								Think you can haggle a better deal?
								<br />
								Prove it online and unlock exclusive savings.
							</h2>

							<h3 className="text-xl sm:text-2xl font-medium text-purpleBrand-300 mb-8 sm:mb-12 lg:mb-16 px-4">
								Exclusive events. Real-time price negotiations. Big brands and
								hidden gems.
							</h3>

							<div className="text-center">
								<button
									className="bg-purpleBrand-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full cursor-pointer text-sm sm:text-base"
									onClick={() => scrollToSection('signup')}
								>
									Sign Up For Free
								</button>
							</div>
						</div>
					</div>
				</div>
			</section>
			{/* How it Works Section */}
			<section
				id="how-it-works"
				className="py-12 lg:px-50 sm:py-16 lg:py-20 bg-white text-[#091C53]"
			>
				<div className="px-5 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-8 sm:mb-12 lg:mb-16">
						<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold sm:mb-12 px-4 text-purpleBrand-500">
							How it Works
						</h2>

						{/* Central Graphic */}
						<div className="sm:mb-12">
							<img
								src={RobotSlidesImg}
								alt="AI Robot Interface"
								className="w-1/3 max-w-4xl mx-auto"
							/>
						</div>

						{/* Four Step Process */}
						<div className="flex flex-col lg:flex-row items-center justify-center gap-4 lg:gap-8">
							{/* Step 1 */}
							<div className="text-center">
								<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
									<FaUserPlus className="w-6 h-6 sm:w-8 sm:h-8 text-purpleBrand-500" />
								</div>
								<h3 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4 px-2">
									Join for Free
								</h3>
								<p className="text-sm sm:text-base px-2">
									Get instant access to negotiation events and offers from
									leading merchants
								</p>
							</div>

							{/* Arrow 1 */}
							<div className="hidden lg:flex items-center justify-center">
								<FaArrowRight className="w-4 h-4 text-purpleBrand-500" />
							</div>

							{/* Step 2 */}
							<div className="text-center">
								<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
									<FaShoppingBag className="w-6 h-6 sm:w-8 sm:h-8 text-purpleBrand-500" />
								</div>
								<h3 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4 px-2">
									Pick Your Deal
								</h3>
								<p className="text-sm sm:text-base px-2">
									See upcoming events from big-name brands and boutique stores
								</p>
							</div>

							{/* Arrow 2 */}
							<div className="hidden lg:flex items-center justify-center">
								<FaArrowRight className="w-4 h-4 text-purpleBrand-500" />
							</div>

							{/* Step 3 */}
							<div className="text-center">
								<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
									<FaComments className="w-6 h-6 sm:w-8 sm:h-8 text-purpleBrand-500" />
								</div>
								<h3 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4 px-2">
									Make Your Move
								</h3>
								<p className="text-sm sm:text-base px-2">
									Negotiate in real time with like chatting with the store
									owner, open and ready to personalise the price to your budget
								</p>
							</div>

							{/* Arrow 3 */}
							<div className="hidden lg:flex items-center justify-center">
								<FaArrowRight className="w-4 h-4 text-purpleBrand-500" />
							</div>

							{/* Step 4 */}
							<div className="text-center">
								<div className="flex items-center justify-center mx-auto mb-4 sm:mb-6">
									<FaCheckCircle className="w-6 h-6 sm:w-8 sm:h-8 text-purpleBrand-500" />
								</div>
								<h3 className="text-lg sm:text-xl font-medium mb-3 sm:mb-4 px-2">
									Lock It In
								</h3>
								<p className="text-sm sm:text-base px-2">
									Walk away with an exclusive deal that feels like a win - for
									you and the merchant
								</p>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* Welcome to Haggle Section */}
			<section
				id="welcome-to-haggle"
				className="px-5 py-12 sm:py-16 lg:py-20 text-[#091C53] relative"
				style={{
					backgroundImage: `url(${CircuitImg})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				<div className="absolute inset-0 bg-white opacity-80"></div>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-20">
					<div className="text-center mb-12 sm:mb-16">
						<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-6 sm:mb-8 text-purpleBrand-500">
							Welcome to Haggle - The New Era of Online Shopping
						</h2>

						<p className="text-base sm:text-lg text-[#091C53] mb-6">
							Shopping has always been one-way: businesses set the price, and
							you either take it or leave it.
						</p>

						<p className="text-xl sm:text-2xl font-semibold text-[#091C53] mb-8">
							But what if you could flip the script?
						</p>

						<p className="text-base sm:text-lg text-gray-700 max-w-4xl mx-auto leading-relaxed">
							Haggle lets you test your skills in live negotiation events with
							top retailers. It's fun, interactive, and designed to get you
							exclusive deals that match your budget - without endless discount
							codes or fake "loyalty" perks.
						</p>
					</div>

					{/* Picture This Section */}
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-12 items-center px-30">
						<div className="order-2 lg:order-1">
							<p className="text-base sm:text-lg italic mb-4 text-purpleBrand-500">
								Picture this:
							</p>
							<p className="text-base sm:text-lg italic text-[#091C53]">
								The Samsung Phone that's just one model behind the latest -
								brand new, still sealed, and now finally within your reach.
							</p>
						</div>

						<div className="order-1 lg:order-2 flex justify-center lg:justify-end">
							<img
								src={SamsungFoldImg}
								alt="Futuristic digital background"
								className="w-2/3 max-w-md"
							/>
						</div>
					</div>
				</div>
			</section>
			{/* Why Shoppers Love It Section */}
			<section
				id="benefits"
				className="px-5 py-12 sm:py-16 lg:py-20 bg-white text-[#091C53]"
			>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="text-center mb-8 sm:mb-12 lg:mb-16">
						<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12 px-4 text-purpleBrand-500">
							Why Shoppers Love It
						</h2>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
						{/* Left side - Visual */}
						<div className="flex justify-center order-2 lg:order-1">
							<div className="relative">
								<img
									src={NegotiateGirlImg}
									alt="Woman negotiating with AI shopping assistant"
									className="w-full max-w-sm sm:max-w-md h-auto"
								/>
								<div className="absolute bottom-4 left-4 bg-white rounded-lg p-3 shadow-lg">
									<div className="flex items-center space-x-2">
										<div className="w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center">
											<div className="w-3 h-3 bg-white rounded-full"></div>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Right side - Benefits */}
						<div className="space-y-6 sm:space-y-8 order-1 lg:order-2">
							<div className="flex items-start space-x-4">
								<div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
									<div className="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center">
										<div className="w-3 h-3 bg-white rounded-full"></div>
									</div>
								</div>
								<div>
									<h3 className="text-base sm:text-lg font-medium mb-2">
										Prices that adjust your budget
									</h3>
								</div>
							</div>

							<div className="flex items-start space-x-4">
								<div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
									<FaClock className="w-6 h-6 text-purpleBrand-500" />
								</div>
								<div>
									<h3 className="text-base sm:text-lg font-medium mb-2">
										Exclusive events that disappear fast
									</h3>
								</div>
							</div>

							<div className="flex items-start space-x-4">
								<div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
									<FaComments className="w-6 h-6 text-purpleBrand-500" />
								</div>
								<div>
									<h3 className="text-base sm:text-lg font-medium mb-2">
										Deals on premium products from big-name and boutique brands
									</h3>
								</div>
							</div>

							<div className="flex items-start space-x-4">
								<div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
									<FaGamepad className="w-6 h-6 text-purpleBrand-500" />
								</div>
								<div>
									<h3 className="text-base sm:text-lg font-medium mb-2">
										A shopping experience that feels like a game - but with real
										rewards
									</h3>
								</div>
							</div>

							<div className="flex items-start space-x-4">
								<div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
									<FaShieldAlt className="w-6 h-6 text-purpleBrand-500" />
								</div>
								<div>
									<h3 className="text-base sm:text-lg font-medium mb-2">
										Peace of mind - merchants are vetted, card details are never
										stored and are processed securely
									</h3>
								</div>
							</div>

							<div className="text-center lg:text-left">
								<button
									className="bg-purpleBrand-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full transition-colors cursor-pointer text-sm sm:text-base"
									onClick={() => scrollToSection('signup')}
								>
									Join Now For Free
								</button>
							</div>
						</div>
					</div>

					{/* Footer Tagline */}
					<div className="mt-12 sm:mt-16">
						<div className="bg-blue-100 rounded-lg p-6 sm:p-8 text-center">
							<p className="text-base sm:text-lg font-bold italic text-[#091C53]">
								From a new sofa to wireless headphones,
							</p>
							<p className="text-base sm:text-lg font-bold italic text-[#091C53]">
								Haggle is where "just out of budget" finally becomes yours.
							</p>
						</div>
					</div>
				</div>
			</section>

			{/* Why Haggle Was Created Section */}
			<section className="px-5 py-12 sm:py-16 lg:py-20 bg-white text-[#091C53]">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 items-center">
						{/* Left side - Text content */}
						<div className="space-y-6 sm:space-y-8">
							<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-purpleBrand-500">
								Why Haggle Was Created
							</h2>

							<p className="text-base sm:text-lg leading-relaxed">
								Haggle was created by retailers who were tired of endless
								discount wars and fake loyalty schemes.
							</p>

							<p className="text-base sm:text-lg leading-relaxed">
								As a leading AI company, we designed a negotiation engine that
								helps customers and merchants truly connect -{' '}
								<strong>
									understanding real needs and budgets before pushing another
									sale.
								</strong>
							</p>

							<p className="text-base sm:text-lg leading-relaxed">
								This is the <strong>new era of shopping</strong>: smarter,
								fairer, and more rewarding for everyone.
							</p>
						</div>

						{/* Right side - Robotic hand image */}
						<div className="flex justify-center order-2 lg:order-1">
							<div className="relative">
								<img
									src={BulbImg}
									alt="AI Robot Hand with Lightbulb"
									className="w-full max-w-sm sm:max-w-md h-auto"
								/>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ Section */}
			<section
				id="faq"
				className="px-5 py-12 sm:py-16 lg:py-20 text-[#333366] relative"
				style={{
					backgroundImage: `url(${JoinImageImg})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				<div className="absolute inset-0 bg-white opacity-80"></div>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
					<div className="text-left mb-8 sm:mb-12 lg:mb-16">
						<h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-8 sm:mb-12 px-4 text-purpleBrand-500">
							Frequently Asked Questions (FAQs)
						</h2>
					</div>

					<div className="max-w-4xl space-y-6 sm:space-y-8">
						{faqData.map((faq, index) => (
							<div key={index} className="text-left">
								<button
									className="w-full text-left flex items-start gap-3 py-2 hover:opacity-80 transition-opacity duration-200"
									onClick={() => toggleFAQ(index)}
								>
									<FaChevronDown
										className={`w-4 h-4 text-[#333366] mt-1 flex-shrink-0 transition-transform duration-200 ${
											openFAQ === index ? 'rotate-180' : ''
										}`}
									/>
									<span className="text-xl sm:text-2xl font-bold text-[#333366]">
										{faq.question}
									</span>
								</button>
								{openFAQ === index && (
									<div className="ml-7 mt-2">
										<p className="text-base sm:text-lg text-[#333366] leading-relaxed">
											{faq.answer.includes(
												'big-name retailers and boutique brands',
											) ? (
												<>
													A mix of{' '}
													<strong>
														big-name retailers and boutique brands
													</strong>{' '}
													across fashion, beauty, home, sport, electronics,
													travel and more.
												</>
											) : (
												faq.answer
											)}
										</p>
									</div>
								)}
							</div>
						))}
					</div>

					<div className="text-left mt-8 sm:mt-12">
						<button
							className="bg-purpleBrand-500 text-white px-6 sm:px-8 py-2 sm:py-3 rounded-full transition-colors cursor-pointer text-sm sm:text-base"
							onClick={() => scrollToSection('signup')}
						>
							Sign Up for Free
						</button>
					</div>
				</div>
			</section>

			{/* Mailchimp Signup Form Section */}
			<section
				id="signup"
				className="min-h-screen py-12 sm:py-16 lg:py-20 bg-white text-[#091C53] flex items-center justify-start"
				style={{
					backgroundImage: `url(${MailchimpFormBgImg})`,
					backgroundSize: 'cover',
					backgroundPosition: 'center',
					backgroundRepeat: 'no-repeat',
				}}
			>
				<div className="absolute inset-0 bg-black opacity-70"></div>
				<div className="max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10 lg:ms-30">
					<div
						id="mc_embed_shell"
						dangerouslySetInnerHTML={{
							__html: `
                    <link href="//cdn-images.mailchimp.com/embedcode/classic-061523.css" rel="stylesheet" type="text/css">
                    <style type="text/css">
                          #mc_embed_signup{
                            background: transparent;
                            clear:left;
                            font:14px "Raleway", sans-serif;
                            width: 400px;
                            padding-left: 50px;
                            border-radius: 0;
                            text-align: center;
                          }
                          #mc_embed_signup h2 {
                            width: 50%;
                            color: #B387D7;
                            font-size: 32px;
                            font-weight: bold;
                            margin-bottom: 25px;
                            text-align: start;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                          }
                          #mc_embed_signup .indicates-required {
                            display: none;
                          }
                          #mc_embed_signup .mc-field-group {
                            margin-bottom: 20px;
                          }
                          #mc_embed_signup label {
                            color: white;
                            font-size: 20px;
                            margin-bottom: 8px;
                            padding-left: 5px;
                            display: block;
                            text-align: start;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                          }
                          #mc_embed_signup input[type="email"],
                          #mc_embed_signup input[type="text"] {
                            width: 100%;
                            padding: 10px;
                            border: none;
                            border-radius: 30px;
                            background: rgba(255, 255, 255, 0.9);
                            font-size: 16px;
                            box-sizing: border-box;
                            margin-top: 5px;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                            box-shadow: 4px 4px 10px 0px rgba(153, 166, 206, 0.3) inset, -4px -4px 4px 5px rgba(255, 255, 255, 1) inset;
                          }
                          #mc_embed_signup input[type="submit"] {
                            background: #653AAA;
                            color: white;
                            border: none;
                            border-radius: 30px;
                            font-size: 16px;
                            font-weight: 600;
                            cursor: pointer;
                            width: 95%;
                            height: 50px;
                            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                            transition: background-color 0.3s ease;
                          }
                          #mc_embed_signup input[type="submit"]:hover {
                            background: #8A5BB8;
                          }
                          #mc_embed_signup .asterisk {
                            display: none;
                          }
                    </style>
                    <div id="mc_embed_signup">
                        <form action="https://haggles.us18.list-manage.com/subscribe/post?u=873a356aea9b8d1faf3200215&amp;id=95af4c9bb0&amp;f_id=008eb7e6f0" method="post" id="mc-embedded-subscribe-form" name="mc-embedded-subscribe-form" class="validate" target="_self" novalidate="">
                            <div id="mc_embed_signup_scroll"><h2>Join The Future Of Shopping</h2>
                                <div class="indicates-required"><span class="asterisk">*</span> indicates required</div>
                                <div class="mc-field-group"><label for="mce-MMERGE7">Full Name</label><input type="text" name="MMERGE7" class=" text" id="mce-MMERGE7" value=""></div><div class="mc-field-group"><label for="mce-EMAIL">Email Address <span class="asterisk">*</span></label><input type="email" name="EMAIL" class="required email" id="mce-EMAIL" required="" value=""></div>
                            <div id="mce-responses" class="clear">
                                <div class="response" id="mce-error-response" style="display: none;"></div>
                                <div class="response" id="mce-success-response" style="display: none;"></div>
                            </div><div aria-hidden="true" style="position: absolute; left: -5000px;"><input type="text" name="b_873a356aea9b8d1faf3200215_95af4c9bb0" tabindex="-1" value=""></div><div class="clear"><input type="submit" name="subscribe" id="mc-embedded-subscribe" class="button" value="Sign Up For Free"></div>
                        </div>
                    </form>
                  </div>
               `,
						}}
					/>
				</div>
			</section>
		</div>
	);
}

export default Welcome;
